import { useState } from "react";

import {
  type Active,
  type CollisionDetection,
  DndContext as DndKitContext,
  type DndContextProps as DndKitContextProps,
  type DragEndEvent,
  DragOverlay,
  type DragOverlayProps,
  MeasuringStrategy,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";

import { IgnoreInteractiveSensor } from "./dndSensors";

/**
 * This function creates a custom collision detection algorithm which ensures that draggables only collide with droppables on the same hierarchy level as themselves. We never want to drop a draggable into a droppable that is not in the same hierarchy level as the draggable.
 * This is useful for example when you have a nested structure of droppables and draggables and you want to ensure that only the correct hierarchy is considered.
 * E.g., for the project board lanes would be hierarchy level 1, whereas cards would be hierarchy level 2.
 */
const createSameHierarchyEnforcementAlgorithm = (
  originalCollisionDetection: DndKitContextProps["collisionDetection"] = closestCenter,
): CollisionDetection => {
  return (args) => {
    const droppableChildToParent: Record<string, string> = {};
    args.droppableContainers.forEach((droppable) => {
      const droppableParent = droppable.data.current.sortable
        .containerId as string;
      droppableChildToParent[droppable.id as string] = droppableParent;
    });

    const droppableHierarchyIndexes = {};
    Object.keys(droppableChildToParent).forEach((droppableId) => {
      let droppableParent = droppableChildToParent[droppableId];
      let index = 0;
      while (droppableParent) {
        if (!droppableHierarchyIndexes[droppableParent]) {
          droppableHierarchyIndexes[droppableParent] = index;
        }
        index++;
        droppableParent = droppableChildToParent[droppableParent];
      }
      droppableHierarchyIndexes[droppableId] = index;
    });

    args.droppableContainers = args.droppableContainers.filter((droppable) => {
      const activeId = args.active.id as string;
      const activeIndex = droppableHierarchyIndexes[activeId];

      const droppableId = droppable.id as string;
      const droppableIndex = droppableHierarchyIndexes[droppableId];

      if (activeIndex === droppableIndex) {
        return true;
      }
      args.droppableRects.delete(droppable.id);
      return false;
    });

    const collisions = originalCollisionDetection(args);

    return collisions;
  };
};

/**
 * DndContext is a wrapper around the DndKit DndContext component.
 * It provides additional functionality for handling drag and drop events,
 */
type DndContextProps = Omit<DndKitContextProps, "onDragEnd"> & {
  overlayProps?: DragOverlayProps;
  onDragEnd?: (
    e: DragEndEvent,
    dropInfo: { originalDroppableId: string },
  ) => void;
};

export const DndContext = ({
  children,
  overlayProps,
  onDragStart,
  onDragEnd,
  modifiers,
  collisionDetection,
  ...rest
}: DndContextProps) => {
  const [active, setActive] = useState<Active>(null);
  const [originalDroppableId, setOriginalDroppableId] = useState<string>(null);
  const [dropZoneRect, setDropZoneRect] = useState<{ x: number; y: number }>();

  const pointerSensor = useSensor(IgnoreInteractiveSensor, {
    activationConstraint: { distance: 2 },
  });
  const sensors = useSensors(pointerSensor);

  return (
    <DndKitContext
      onDragStart={(e) => {
        setActive(e.active);
        setOriginalDroppableId(
          e.active.data.current.sortable.containerId as string,
        );
        onDragStart?.(e);
      }}
      onDragEnd={(e) => {
        if (e.over?.id) {
          const activeRect = e.active.rect.current.initial;
          const destRect = e.over.rect;

          let yOffset = destRect.top;
          let xOffset = destRect.left;

          // Because we can have different sizes of draggables we need to adjust the position of the interpolated drop position by the difference of their sizes when moving down or right because the size difference will otherwise cause the position to be off.
          const didMoveDown = activeRect.top < destRect.top;
          if (didMoveDown) {
            if (activeRect.height > destRect.height) {
              yOffset = destRect.top - (activeRect.height - destRect.height);
            } else {
              yOffset = destRect.top + (destRect.height - activeRect.height);
            }
          }

          const didMoveRight = activeRect.left < destRect.left;
          if (didMoveRight) {
            if (activeRect.width > destRect.width) {
              xOffset = destRect.left - (activeRect.width - destRect.width);
            } else {
              xOffset = destRect.left + (destRect.width - activeRect.width);
            }
          }

          setDropZoneRect({ x: xOffset, y: yOffset });
        }

        onDragEnd?.(e, { originalDroppableId });
        setOriginalDroppableId(null);

        setActive((prev) => (prev === e.active ? null : prev));
        requestAnimationFrame(() => {
          setDropZoneRect(null);
        });
      }}
      collisionDetection={createSameHierarchyEnforcementAlgorithm(
        collisionDetection,
      )}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      sensors={sensors}
      modifiers={modifiers ?? [restrictToWindowEdges]}
      {...rest}
    >
      {children}
      {active ?
        <DragOverlay
          /**
           * We interpolate the keyframes for the drag overlay animation ourselves.
           * If the position didn't change (no "over") we use the default configuration.
           * If the position changed (we are over a droppable), we use the position of the droppable as the final position and always animate there, no matter what the state transition is.
           */
          dropAnimation={{
            keyframes: (resolver) => {
              return [
                {
                  transform: `translate3d(${resolver.transform.initial.x}px,
                    ${resolver.transform.initial.y}px, 0)`,
                },
                {
                  ...(dropZoneRect ?
                    { position: "fixed", top: "0", left: "0" }
                  : {}),
                  transform:
                    dropZoneRect ?
                      `translate3d(${dropZoneRect?.x}px,
                        ${dropZoneRect?.y}px, 0)`
                    : `translate3d(${resolver.transform.final.x}px,
                        ${resolver.transform.final.y}px, 0)`,
                },
              ];
            },
          }}
          style={{ zIndex: 99_999, ...(overlayProps?.style ?? {}) }}
          {...overlayProps}
        >
          {active.data.current.overlay}
        </DragOverlay>
      : null}
    </DndKitContext>
  );
};
